import { observer } from 'mobx-react-lite';
import { ChangeEvent, useEffect, useState } from 'react';
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input/mobile';

import 'react-phone-number-input/style.css';

import { useMst } from '../../../../models/rootModel';

import { capitalizeFirstLetter, isMobileAppWebView } from '../../../../utils/utilsFunctions';

import { InputField } from '../../../common/InputField/InputField';
import { TermsOfServicePopUp } from '../../../common/TermsOfServicePopUp';
import { ErrorCode, ServerError, SignUpFields, SignUpStepProps, SignUpValues } from '../SignUp.types';
import { SignUpErrorPopUp } from '../SignUpErrorPopUp';

import { toJS } from 'mobx';
import rightColImage from '../../../../assets/womenHoldingHat.webp';
import { useAnalytics } from '../../../../utils/analytics';
import { generateRandomNumber } from '../../../../utils/dataGenerators/generateRandomNumber';
import { generateRandomString } from '../../../../utils/dataGenerators/generateRandomString';
import { sendGTMEvent } from '../../../../utils/googleTagManager';
import { LogoWithName } from '../../../common/LogoWithName';
import { MobilePaymentSummary } from '../../../common/PaymentSummary/MobilePaymentSummary';
import { PaymentSummary } from '../../../common/PaymentSummary/PaymentSummary';
import { BaseStepV2 } from '../../../common/v2/BaseStepV2';
import { StepV2MainContent } from '../../../common/v2/StepV2MainContent';
import { ColWithBackgroundImage } from '../../../common/v2/TwoSidedLayout/ColWithBackgroundImage';
import { ContentBoxWrapper, MainContentBox } from '../../../common/v2/Box/Box.style';
import {
  ErrorInputText,
  InputRoot,
  Label,
  SpanLink,
  TermsLabel,
} from '../../../styledComponent/SignUpComponents.style';
import { SignUpBottomTextInput } from '../../../styledComponent/Text.style';
import { planDataExtractor } from '../../PaymentStep/planDataExtractor';
import { ContactDetailsPaymentSummaryCol, ContactDetailsTwoSidedLayout } from './ContactDetailsStep.style';
import { ContentCol } from '../../../common/v2/TwoSidedLayout/TwoSidedLayout.style';

export const ContactDetailsStep = observer((props: SignUpStepProps) => {
  const analytics = useAnalytics();

  const useMockedSignupData = process.env.REACT_APP_USE_MOCKED_SIGNUP_DATA === '1';

  const { inputFields, nextStep, isActive, showPaymentSummary } = props;
  const { UserDiscoveryResponse, UserPayment } = useMst();
  const [isValidInputs, setIsValidInputs] = useState(false);
  const [isPopUpOpen, setIsPopUpOpen] = useState(false);
  const [serverErrorMessage, setServerErrorMessage] = useState<ServerError>();
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);
  const [isTermsPopUpOpen, setIsTermsPopUpOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const plan = UserPayment.getChoosenPlan();
  const currentPlan = planDataExtractor(plan);
  const discount = toJS(UserPayment.discount);

  useEffect(() => {
    if (isActive) {
      analytics.track('view contact info page');
      sendGTMEvent({
        event: 'Signup_Enter_Details',
      });
      window.scrollTo(0, 0);
    }
  }, [isActive, analytics]);

  const [signUpValues, setSignUpValues] = useState<SignUpValues>({
    [SignUpFields.Name]: useMockedSignupData ? generateRandomString(6) : '',
    [SignUpFields.Phone]: useMockedSignupData
      ? `+1202${generateRandomNumber(100, 999)}${generateRandomNumber(1000, 9999)}`
      : '',
    [SignUpFields.Email]: useMockedSignupData ? `circlestest@${Math.random()}.adasd` : '',
  });

  const [signUpInputError, setSignUpInputError] = useState<SignUpValues>({
    [SignUpFields.Name]: '',
    [SignUpFields.Phone]: '',
    [SignUpFields.Email]: '',
  });

  useEffect(() => {
    const checkValidInputs =
      signUpValues[SignUpFields.Name].length > 0 &&
      signUpValues[SignUpFields.Email].length > 0 &&
      signUpValues[SignUpFields.Phone].length > 0;
    checkValidInputs ? setIsValidInputs(true) : setIsValidInputs(false);
  }, [signUpValues]);

  useEffect(() => {
    setSubmitButtonDisabled(!isValidInputs);
  }, [isValidInputs]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    // Doesn't allow to enter number or other illegal chars, allows space, letters and ' -
    if (name === 'name' && /([\d!@#$%^&*()_+={}"|\\/[\]`,.?])+/.test(value)) {
      return;
    }
    setSignUpInputError((prevValues) => ({
      ...prevValues,
      [name]: '',
    }));

    setSignUpValues((prevState) => {
      return { ...prevState, [name]: value };
    });
  };

  const phoneChange = (value: string | undefined) => {
    setSignUpInputError((prevValues) => ({
      ...prevValues,
      [SignUpFields.Phone]: '',
    }));
    if (typeof value === 'string') {
      setSignUpValues((prevState) => {
        return { ...prevState, [SignUpFields.Phone]: value };
      });
    }
  };

  const onSubmit = async () => {
    const parsedPhone = parsePhoneNumber(signUpValues[SignUpFields.Phone]);
    if (parsedPhone) {
      const params = {
        phone: parsedPhone.nationalNumber,
        country_code: `+${parsedPhone.countryCallingCode}`,
        email: signUpValues[SignUpFields.Email],
      };
      const urlParams = new URLSearchParams(params);
      try {
        const resCanUserSignUp = await UserDiscoveryResponse.canUserSignUp(urlParams.toString());
        if (resCanUserSignUp.kind !== 'ok') {
          setIsLoading(false);
          throw new Error(resCanUserSignUp.data.error || ErrorCode.InternalServerError);
        }
        if (props.totalSteps !== props.stepIndex + 1) {
          sendGTMEvent({
            event: 'signup_enter_details',
            phone: signUpValues[SignUpFields.Phone],
            full_name: signUpValues[SignUpFields.Name],
            email: signUpValues[SignUpFields.Email],
          });

          setIsLoading(false);
          setIsValidInputs(false);
          // using this step we want to create the account only after user tries to pay
          // so if the user can signup (there is no existing user with those credentials) we mark this to true
          // so the payment steps can behave accordingly (load only stripe plans at start, and other stripe info on pay action)
          UserPayment.setCreateAccountOnPay(true);
          analytics.track('contact info page update success');
          nextStep();
        }
      } catch (error) {
        if (error instanceof Error) {
          const message =
            error.message === ErrorCode.Exists
              ? 'Welcome back! We found an existing account with your contact details. Please click on the button below to log back in.'
              : 'Sorry, something went wrong...';
          setServerErrorMessage({ message, errorCode: ErrorCode.Exists });
          setIsValidInputs(false);
          setIsPopUpOpen(true);
          setIsLoading(false);
        }
      }
    }
  };

  const validatedSignUpInformation = () => {
    setIsLoading(true);
    let isThereError = false;
    analytics.track('click contact info page submit');

    if (!isValidPhoneNumber(signUpValues[SignUpFields.Phone])) {
      isThereError = true;
      setSignUpInputError((prevValues) => ({
        ...prevValues,
        phone: 'Please enter your correct phone number',
      }));
    }

    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(signUpValues[SignUpFields.Email])) {
      isThereError = true;
      setSignUpInputError((prevValues) => ({
        ...prevValues,
        email: 'Please enter a valid email. E.g. name@domain.com',
      }));
    }

    if (signUpValues[SignUpFields.Email] === 'name@domain.com') {
      isThereError = true;
      setSignUpInputError((prevValues) => ({
        ...prevValues,
        email: 'Use your real email :)',
      }));
    }

    if (isThereError) {
      setIsValidInputs(false);
      setIsLoading(false);
      return;
    }

    UserDiscoveryResponse.setUserNameAndEmail({
      email: signUpValues[SignUpFields.Email],
      name: signUpValues[SignUpFields.Name],
    });
    UserDiscoveryResponse.setUserPhoneNumber(signUpValues[SignUpFields.Phone]);

    setIsValidInputs(true);
    setSubmitButtonDisabled(true);
    onSubmit();
  };

  const onClosePopUp = () => {
    setIsPopUpOpen(false);
    if (serverErrorMessage?.errorCode === ErrorCode.Exists) {
      window.open(
        `${process.env.REACT_APP_API_LOGIN}login/?loginRedirectUrl=${window.location.origin}/onboarding/document/payment`,
        '_self'
      );
    }
  };

  const openTerms = () => {
    if (!isMobileAppWebView) {
      window.open(`${process.env.REACT_APP_WEBSITE}/terms-of-use/`, '_blank');
      return;
    }
  };
  const openPrivacy = () => {
    if (!isMobileAppWebView) {
      window.open(`${process.env.REACT_APP_WEBSITE}/privacy/`, '_blank');
      return;
    }
  };

  const idStart = 'signUpForm';

  const canShowPaymentSummary = Boolean(showPaymentSummary && plan);

  if (!isActive) {
    return null;
  }

  return (
    <BaseStepV2 {...props}>
      <TermsOfServicePopUp open={isTermsPopUpOpen} handleClose={() => setIsTermsPopUpOpen(false)} />

      {canShowPaymentSummary && (
        <MobilePaymentSummary
          planName={currentPlan.sanitizedDescription}
          planPriceInCents={currentPlan.sanitizedAmount}
          planMonthlyPriceShort={currentPlan.sanitizedMonthlyPriceShort}
          planWeeklyPriceShort={currentPlan.sanitizedWeeklyPriceShort}
          appliedDiscount={discount}
          planCurrency={currentPlan.sanitizedCurrency}
          showCouponForm={true}
          planPriceWithoutBargainShort={currentPlan.sanitizedPriceWithoutBargainShort}
        />
      )}

      <ContactDetailsTwoSidedLayout showPaymentSummary={Boolean(canShowPaymentSummary)}>
        <ContentCol location="left">
          <ContentBoxWrapper>
            <StepV2MainContent
              {...props}
              isButtonDisabled={submitButtonDisabled}
              isLoading={isLoading}
              onButtonClick={validatedSignUpInformation}
            >
              {inputFields.map((inputField, index) => (
                <InputRoot key={`${inputField.type}-${index}`}>
                  <Label>{inputField.label}</Label>
                  {inputField.type !== SignUpFields.Phone ? (
                    <InputField
                      id={`${idStart}${capitalizeFirstLetter(inputField.type)}`}
                      type={inputField.type === SignUpFields.Name ? 'text' : 'email'}
                      error={signUpInputError[inputField.type]}
                      name={inputField.type}
                      maxLength={30}
                      handleChange={(event: ChangeEvent<HTMLInputElement>) => handleChange(event)}
                      value={signUpValues[inputField.type]}
                      placeholder={inputField.placeHolder}
                      bottomText={inputField.explanation ? inputField.explanation : ''}
                    />
                  ) : (
                    <>
                      <PhoneInput
                        id={`${idStart}${capitalizeFirstLetter(inputField.type)}`}
                        onChange={(value: string | undefined) => phoneChange(value)}
                        value={signUpValues[SignUpFields.Phone]}
                        placeholder={inputField.placeHolder}
                        defaultCountry={'US'}
                        maxLength={16}
                      />
                      {inputField.explanation && signUpInputError[inputField.type].length === 0 ? (
                        <SignUpBottomTextInput>{inputField.explanation}</SignUpBottomTextInput>
                      ) : (
                        ''
                      )}
                    </>
                  )}
                  {signUpInputError[inputField.type].length > 0 ? (
                    <ErrorInputText>{signUpInputError[inputField.type]}</ErrorInputText>
                  ) : (
                    ''
                  )}
                </InputRoot>
              ))}
              <TermsLabel>
                By continuing, you agree to the Circles <SpanLink onClick={openTerms}>terms of use</SpanLink> and <SpanLink onClick={openPrivacy}>privacy policy</SpanLink>
              </TermsLabel>
            </StepV2MainContent>
          </ContentBoxWrapper>
        </ContentCol>
        {canShowPaymentSummary && (
          <ContactDetailsPaymentSummaryCol location="right">
            <ContentBoxWrapper offsetVariant={80}>
              <MainContentBox>
                <LogoWithName />
                <PaymentSummary
                  planName={currentPlan.sanitizedDescription}
                  planPriceInCents={currentPlan.sanitizedAmount}
                  planMonthlyPriceShort={currentPlan.sanitizedMonthlyPriceShort}
                  planWeeklyPriceShort={currentPlan.sanitizedWeeklyPriceShort}
                  appliedDiscount={discount}
                  planCurrency={currentPlan.sanitizedCurrency}
                  showCouponForm={true}
                />
              </MainContentBox>
            </ContentBoxWrapper>
          </ContactDetailsPaymentSummaryCol>
        )}
        {!canShowPaymentSummary && <ColWithBackgroundImage img={rightColImage} logoColorVariant="black" />}
      </ContactDetailsTwoSidedLayout>

      <SignUpErrorPopUp
        setIsPopUpOpen={setIsPopUpOpen}
        isPopUpOpen={isPopUpOpen}
        onClosePopUp={onClosePopUp}
        serverErrorMessage={serverErrorMessage}
      />
    </BaseStepV2>
  );
});
